/** @format */

import React, { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import withStyles from '@material-ui/core/styles/withStyles';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { TableBody, TableFooter, TableHead, TableRow } from '@material-ui/core';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import TableCell from '@material-ui/core/TableCell';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import Pager from '../../../components/Adescom/PagerRange';
import I18n from '../../../components/Translate/I18n';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import PaperedTable from '../../../components/Table/PaperedTable';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import EditButton from '../../../components/Buttons/EditButton';
import DeleteButton from '../../../components/Buttons/DeleteButton';
import { AlertDialog } from '../../lib/dialog/AlertDialog';
import { tariffConstants } from '../../../modules/tariffs/actions';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import { configDefault } from '../../../core/config';
import PlatformMsisdnRangePoolAdd from './PlatformMsisdnRangePoolAdd';

interface PlatformMsisdnRangePoolListProps {
    classes: any;
    dispatch: any;

    showMainLoader(value: boolean): void;
}

interface PlatformMsisdnRangePoolListState {
    filters: Array<Filter>;
    orders: Array<Order>;
    pools: any;
    selectedIdToDelete: number | null;
    deleteMessage: string | null;
    addMode: boolean;
    editMode: boolean;
    selectedPool: any;
}

class PlatformMsisdnRangePoolList extends PureComponent<PlatformMsisdnRangePoolListProps, PlatformMsisdnRangePoolListState> {
    private itemLoader: ItemLoaders;

    constructor(props: PlatformMsisdnRangePoolListProps, context: PlatformMsisdnRangePoolListState) {
        super(props, context);

        this.state = {
            filters: [{ name: 'deleted', value: false }],
            orders: [],
            pools: null,
            selectedIdToDelete: null,
            deleteMessage: null,
            addMode: false,
            editMode: false,
            selectedPool: null,
        };
        this.itemLoader = new ItemLoaders(this.props.dispatch);
    }

    componentDidMount(): void {
        this.firstLoad();
    }

    updateOrderList = (orders: Array<Order>): void => this.setState({ orders: orders }, this.firstLoad);
    updateFilterList = (filters: Array<Filter>): void => this.setState({ filters: filters });

    paginationCallbackFunction = (uri: string): void => {
        this.props.showMainLoader(true);
        this.itemLoader.paginationCallWithCallback(
            uri,
            'load_platform_msisdn_range_pools',
            (data: any): void => {
                this.setState({ pools: data });
                this.props.showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    firstLoad = (): void => {
        const { filters, orders } = this.state;
        this.props.showMainLoader(true);
        this.itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/platform_msisdn_range_pools')),
            'load_platform_msisdn_range_pools',
            filters,
            orders,
            (data: any): void => {
                this.setState({ pools: data });
                this.props.showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    makeEdit = (params: any): void => {
        this.setState({
            editMode: true,
            selectedPool: params.item,
        });
    };

    makeDelete = (params: { id: number }): void => {
        this.setState({
            selectedIdToDelete: params.id,
            deleteMessage: 'pools:h.delete_message',
        });
    };

    confirmDelete = (): void => {
        const { selectedIdToDelete } = this.state;
        const { dispatch } = this.props;

        this.itemLoader.deleteCall(
            FormGeneralHelper.getByUri(
                FormGeneralHelper.getPath(`/platform_msisdn_range_pools/${selectedIdToDelete}`, configDefault.PLATFORM_API_PREFIX)
            ),
            tariffConstants.DELETE,
            (data: any) => {
                this.setState({
                    selectedIdToDelete: null,
                    deleteMessage: null,
                });
                NotificationHelper.notifySuccess('pools:notify.delete_success', dispatch);
                this.firstLoad();
            },
            { dispatchAfterCallback: false }
        );
    };

    addNewPool = (): void => {
        this.setState({
            addMode: true,
            editMode: false,
            selectedPool: null,
        });
    };

    formatDate = (dateString: string): string => {
        if (!dateString) {
            return '';
        }
        const date = new Date(dateString);
        return date.toLocaleString('pl-PL');
    };

    render(): ReactNode {
        const { classes } = this.props;
        const { filters, orders, pools, deleteMessage, addMode, editMode, selectedPool } = this.state;

        const tableStickyHeader = { root: classes.tableStickyHeader };

        if (addMode) {
            return (
                <GridContainer>
                    <GridItem xs={12}>
                        <CustomTextButtom
                            callback={(): void => this.setState({ addMode: false })}
                            callbackParam={{}}
                            color="primary"
                            size={'lg'}
                            label="general:common.back"
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <PlatformMsisdnRangePoolAdd
                            classes={classes}
                            editMode={false}
                            onSaveSuccess={(): void => {
                                this.setState({ addMode: false });
                                this.firstLoad();
                            }}
                        />
                    </GridItem>
                </GridContainer>
            );
        }

        if (editMode && selectedPool) {
            return (
                <GridContainer>
                    <GridItem xs={12}>
                        <CustomTextButtom
                            callback={(): void => this.setState({ editMode: false, selectedPool: null })}
                            callbackParam={{}}
                            color="primary"
                            size={'lg'}
                            label="general:common.back"
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <PlatformMsisdnRangePoolAdd
                            classes={classes}
                            pool={selectedPool}
                            editMode={true}
                            onSaveSuccess={(): void => {
                                this.setState({ editMode: false, selectedPool: null });
                                this.firstLoad();
                            }}
                        />
                    </GridItem>
                </GridContainer>
            );
        }

        return (
            <GridContainer>
                <GridItem xs={12}>
                    <PaperedTable
                        beforeTableContent={
                            <GridContainer>
                                <GridItem xs={12} className={classes.beforeTableContent}>
                                    <CustomTextButtom
                                        callback={this.addNewPool}
                                        callbackParam={{}}
                                        color="primary"
                                        size={'lg'}
                                        label="pools:list.add_new_pool"
                                    />
                                    <CustomTextButtom
                                        callback={this.firstLoad}
                                        callbackParam={{}}
                                        color="primary"
                                        size={'lg'}
                                        label="general:common.search"
                                    />
                                </GridItem>
                            </GridContainer>
                        }
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: 80 }}>
                                    <I18n trans="pools:list.actions" />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="pools:list.range_start"
                                        fieldName="rangeStart"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="asc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                        updateOrderList={this.updateOrderList}
                                        updateFilterList={this.updateFilterList}
                                        searchActionCallback={this.firstLoad}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="pools:list.range_end"
                                        fieldName="rangeEnd"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="asc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                        updateOrderList={this.updateOrderList}
                                        updateFilterList={this.updateFilterList}
                                        searchActionCallback={this.firstLoad}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="pools:list.platform_reseller_name"
                                        fieldName="platformResellerName"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="asc"
                                        searchFieldType="string"
                                        filters={filters}
                                        sort={orders}
                                        updateOrderList={this.updateOrderList}
                                        updateFilterList={this.updateFilterList}
                                        searchActionCallback={this.firstLoad}
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} padding="none" align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="pools:pool.created_at"
                                        fieldName="createdAt"
                                        showSort={true}
                                        onlyFilter={false}
                                        defaultDirection="desc"
                                        searchFieldType="date"
                                        dateFormat="RRRR-MM-DD"
                                        beforeSetToMax={true}
                                        afterSetToMin={true}
                                        dateFromTo={true}
                                        breakLine={true}
                                        timeFormat={false}
                                        filters={filters}
                                        sort={orders}
                                        updateOrderList={this.updateOrderList}
                                        updateFilterList={this.updateFilterList}
                                        searchActionCallback={this.firstLoad}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pools &&
                                pools['hydra:member'] &&
                                pools['hydra:member'].map((el: any) => {
                                    return (
                                        <TableRow key={el.id}>
                                            <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: 80 }}>
                                                <EditButton callback={this.makeEdit} callbackParam={{ item: el }} />
                                                <DeleteButton callback={this.makeDelete} callbackParam={{ id: el.id }} />
                                            </TableCell>
                                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                                {el.rangeStart}
                                            </TableCell>
                                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                                {el.rangeEnd}
                                            </TableCell>
                                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                                {el.platformResellerName || '-'}
                                            </TableCell>
                                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                                {this.formatDate(el.createdAt)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {pools && pools['hydra:member'] && pools['hydra:member'].length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <I18n trans="pools:list.no_result" />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={5}>
                                    {pools && pools['hydra:view'] && <Pager callback={this.paginationCallbackFunction} views={pools} />}
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </PaperedTable>
                </GridItem>
                <GridItem xs={12}>
                    {deleteMessage && (
                        <AlertDialog
                            open={!!deleteMessage}
                            titleDialog="pools:h.delete_confirmation"
                            displayMessage={deleteMessage}
                            closeCallback={(): void => {
                                this.setState({
                                    deleteMessage: null,
                                    selectedIdToDelete: null,
                                });
                            }}
                            confirmCallback={this.confirmDelete}
                        />
                    )}
                </GridItem>
            </GridContainer>
        );
    }
}

const stateToProps = (): object => {
    return {};
};

export default compose<any, any>(connect(stateToProps), withStyles(oplAppStyle))(PlatformMsisdnRangePoolList);
